import { useEffect, useState, type FormEvent } from 'react';
import { Check } from 'lucide-react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useTheme } from '../lib/theme';
import { Field } from '../components/ui';
import logoDark from '../assets/proactive-logo-dark.png';
import logoWhite from '../assets/proactive-logo-white.png';

/** Página pública de reset de contraseña (link del email). Valida el token, pide la nueva
 *  contraseña y la fija. Sin sesión. */
export function ResetPassword() {
  const { token } = useParams();
  const { t } = useI18n();
  const { theme } = useTheme();
  const nav = useNavigate();
  const [state, setState] = useState<'loading' | 'ok' | 'invalid' | 'done'>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<{ ok: true; email: string }>(`/api/public/reset/${token}`)
      .then((r) => { setEmail(r.email); setState('ok'); })
      .catch(() => setState('invalid'));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (pw.length < 8) { setError(t('reset.min8')); return; }
    if (pw !== pw2) { setError(t('reset.mismatch')); return; }
    setBusy(true);
    try {
      await api.post(`/api/public/reset/${token}`, { password: pw });
      setState('done');
      setTimeout(() => nav('/login'), 2500);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'invalid_or_expired_token' ? t('reset.expired') : t('reset.error'));
    } finally { setBusy(false); }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">
          <img src={theme === 'dark' ? logoWhite : logoDark} alt="Proactive" className="auth-logo" />
        </div>
        {state === 'loading' && <div className="auth-sub">…</div>}
        {state === 'invalid' && (
          <>
            <div className="auth-title">{t('reset.invalidTitle')}</div>
            <div className="auth-sub">{t('reset.invalidSub')}</div>
            <p style={{ marginTop: 16, fontSize: 'var(--fs-sm)' }} className="muted"><Link to="/login">{t('reset.backLogin')}</Link></p>
          </>
        )}
        {state === 'done' && (
          <>
            <div className="auth-title"><Check size={16} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('reset.doneTitle')}</div>
            <div className="auth-sub">{t('reset.doneSub')}</div>
            <p style={{ marginTop: 16, fontSize: 'var(--fs-sm)' }} className="muted"><Link to="/login">{t('reset.backLogin')}</Link></p>
          </>
        )}
        {state === 'ok' && (
          <>
            <div className="auth-title">{t('reset.title')}</div>
            <div className="auth-sub">{email}</div>
            <form onSubmit={submit}>
              <Field label={t('reset.newPw')} type="password" value={pw} onChange={setPw} />
              <Field label={t('reset.confirmPw')} type="password" value={pw2} onChange={setPw2} />
              {error && <div className="error">{error}</div>}
              <button className="btn" style={{ width: '100%' }} disabled={busy || pw.length < 8}>
                {busy ? '…' : t('reset.setPw')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
